import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import IntlMessages from "util/IntlMessages";
import {
  Card,
  Table,
  Row,
  Col,
  Button,
  Checkbox,
  Space,
  Input,
  Tag,
  Tooltip,
  Modal,
  Menu,
  Dropdown,
  List,
  Tabs,
  Switch,
  message,
} from "antd";
import {
  SearchOutlined,
  InfoCircleTwoTone,
  EyeTwoTone,
  BellTwoTone,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EditTwoTone,
  DeleteTwoTone,
  UsergroupAddOutlined,
  ExpandOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  EllipsisOutlined,
  ExpandAltOutlined
} from "@ant-design/icons";
import { findIndex, has } from "lodash";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./partner-list.css";
import { showConfirm, getRole } from "../../../helpers";
import ConfirmationPopup from "../../../components/ConfirmationPopup";
import CustomPagination from "../../../components/CustomPagination";
import { useConfirmationPopup } from "../../../hooks/useConfirmationPopup";
import {
  useGetPartnerListQuery,
  useDeletePartnerMutation,
  useSendReminderMutation,
  useGetLicensorListQuery,
  useUpdateLicensorMutation,
  useDeleteLicensorMutation,
} from "../../../appRedux/api/adminPartnerApi";
import infoIcon from "assets/images/icons/info.svg";
import eyeIcon from "assets/images/icons/visibility.svg";
import notificationBellIcon from "assets/images/icons/notification.svg";
import deleteTempIcon from "assets/images/icons/delete.svg";
import editIcon from "assets/images/icons/Edit.svg";
import SearchOutlinedIcon from "assets/images/icons/search_outlined.svg";
import SearchFilledIcon from "assets/images/icons/search_filled.svg";
import "./partner-list.css";
import PartnerDrawer from "../../../components/PartnerDrawer";
import LicensorDrawer from "../../../components/LicensorDrawer";
import LoadingBar from "../../../components/LoadingBar";

const { TabPane } = Tabs;

const PartnerList = (props) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "licensor" ? "licensor" : "partner";

  const handleTabChange = (key) => {
    setSearchParams({ tab: key });
  };

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
  });

  const [filterParams, setFilterParams] = useState({
    company_name: undefined
  });

  const [sortOrder, setSortOrder] = useState('company_name'); // Default ascending sort by company name

  // RTK Query hooks with pagination params
  const { data: apiResponse, isLoading, isFetching } = useGetPartnerListQuery({
    limit: pagination.pageSize,
    offset: ((pagination.current || 1) - 1) * pagination.pageSize,
    ordering: sortOrder,
    ...filterParams
  });
  const [deletePartner] = useDeletePartnerMutation();
  const [sendReminder] = useSendReminderMutation();

  // Licensor tab
  const [licensorPagination, setLicensorPagination] = useState({
    current: 1,
    pageSize: 100,
  });
  const {
    data: licensorResponse,
    isLoading: isLicensorLoading,
    isFetching: isLicensorFetching,
  } = useGetLicensorListQuery(
    {
      limit: licensorPagination.pageSize,
      offset: ((licensorPagination.current || 1) - 1) * licensorPagination.pageSize,
    },
    { skip: activeTab !== "licensor" }
  );
  const [updateLicensor, { isLoading: isUpdatingLicensor }] = useUpdateLicensorMutation();
  const [deleteLicensor] = useDeleteLicensorMutation();
  const licensorData = licensorResponse?.results || [];
  const licensorTotalCount = licensorResponse?.count || 0;

  const handleLicensorPermissionToggle = (item, field, checked) => {
    updateLicensor({ id: item.id, data: { [field]: checked } });
  };

  const [data, setData] = useState([]);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [activeProductsVisible, setActiveProductsVisible] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productTitle, setProductTitle] = useState("Active Products");
  const [totalCount, setTotalCount] = useState(0);

  // Confirmation popup hook
  const {
    isVisible: confirmationVisible,
    config: confirmationConfig,
    handleConfirm,
    handleCancel,
    showDeleteConfirmation,
  } = useConfirmationPopup();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [licensorDrawerVisible, setLicensorDrawerVisible] = useState(false);
  const [licensorEditMode, setLicensorEditMode] = useState(false);
  const [selectedLicensor, setSelectedLicensor] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);

  useEffect(() => {
    setIsUserAdmin(() => {
      const role = getRole();
      return role === "ADMIN" || role === "DIAGNOSTIC_ADMIN";
    });
  }, []);

  useEffect(() => {
    if (apiResponse) {
      setTotalCount(apiResponse.count || 0);

      // Process the results array
      let rowData = apiResponse.results && apiResponse.results.length
        ? apiResponse.results
            .filter((value) => value.partner)
            .map((value) => ({
              key: value.partner.partner_id,
              name: value.partner.company_name,
              email: value.email,
              active_products: value.partner.active_products || [],
              pdt: getTemplateData(
                value.partner.template_data,
                "pdt",
                value.partner.partner_id,
              ),
              filing_plans: getTemplateData(
                value.partner.template_data,
                "filing plan",
                value.partner.partner_id,
              ),
              sales_report: getTemplateData(
                value.partner.template_data,
                "sales",
                value.partner.partner_id,
              ),
            }))
        : [];
      setData(rowData);
    }
  }, [apiResponse]);

  const getTemplateData = (templateList, type, partner_id) => {
    if (!templateList || !Array.isArray(templateList)) {
      return { partner_id };
    }

    let templateData = templateList[findIndex(templateList, { template_type: type })];
    return templateData ? { ...templateData, partner_id } : { partner_id };
  };

  const handleTableChange = (paginationInfo, filters, sorter) => {
    // Handle pagination
    if (paginationInfo) {
      setPagination({
        current: paginationInfo.current || 1,
        pageSize: paginationInfo.pageSize || 100,
      });
    }

    // Handle sorting
    if (sorter && sorter.field) {
      const sortFieldMap = {
        name: 'company_name', // Map frontend 'name' field to backend 'company_name'
      };

      const backendField = sortFieldMap[sorter.field] || sorter.field;
      const ordering = sorter.order === 'descend' ? `-${backendField}` : backendField;
      setSortOrder(ordering);
    } else {
      setSortOrder('company_name'); // Reset to default ascending sort by company name
    }
  };

  const showDrawer = (mode, partnerId = null) => {
    setEditMode(mode === 'edit');
    setSelectedPartnerId(partnerId);
    setDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setEditMode(false);
    setSelectedPartnerId(null);
  };

  const navigateToAddPartner = () => {
    showDrawer('add');
  };

  const showAddLicensor = () => {
    setLicensorEditMode(false);
    setSelectedLicensor(null);
    setLicensorDrawerVisible(true);
  };

  const showEditLicensor = (item) => {
    setLicensorEditMode(true);
    setSelectedLicensor(item);
    setLicensorDrawerVisible(true);
  };

  const handleLicensorDrawerClose = () => {
    setLicensorDrawerVisible(false);
    setLicensorEditMode(false);
    setSelectedLicensor(null);
  };

  const navigateToContent = (item) => {
    showDrawer('edit', item.key);
  };

  const navigateToPdt = (item) => {
    window.open(
      "/admin/development-timeline/" + item,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const navigateToFilingPlans = (item) => {
    window.open("/admin/filing-plans/" + item, "_blank", "noopener,noreferrer");
  };

  const navigateToSalesReport = (item) => {
    window.open("/admin/sales-report/" + item, "_blank", "noopener,noreferrer");
  };

  const showActiveProducts = (products, companyName) => {
    setSelectedProducts(products);
    setProductTitle(`Active Products - ${companyName}`);
    setActiveProductsVisible(true);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);

    if (dataIndex === 'name') {
      setFilterParams(prev => ({ ...prev, company_name: selectedKeys[0] || undefined }));
      setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page when filtering
    }
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    setSearchText('');
    setSearchedColumn('');

    if (dataIndex === 'name') {
      setFilterParams(prev => ({ ...prev, company_name: undefined }));
    }
  };

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${placeholder}`}
          value={selectedKeys[0] || ''}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      filtered
        ? <img src={SearchFilledIcon} alt="Search Filled" style={{ width: 16, height: 16 }} />
        : <img src={SearchOutlinedIcon} alt="Search Outlined" style={{ width: 16, height: 16 }} />
    ),
    onFilter: (value, record) => {
      if (!value || value.trim() === '') return true;
      return record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '';
    },
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    filteredValue: searchedColumn === dataIndex ? [searchText] : [],
  });

  const setColumns = () => {
    let columns = [
      {
        title: "Company Name",
        dataIndex: "name",
        key: "name",
        align: "center",
        ...getColumnSearchProps("name", "Company Name"),
        defaultSortOrder: "ascend",
        sorter: true,
        width: 200,
        render: (name) => <span className="gx-text-capitalize">{name}</span>,
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        align: "center",
        width: 200,
      },
      {
        title: "Active Products",
        dataIndex: "active_products",
        key: "active_products",
        align: "center",
        width: 300,
        render: (products, record) => {
          const sortedProducts =
            products && products.length > 0
              ? [...products].sort((a, b) =>
                  a.product_name.localeCompare(b.product_name)
                )
              : [];

          return (
            <div className="active-products-cell">
              {sortedProducts.length > 0 ? (
                <>
                  <div className="product-name-group">
                    <span className="product-name">
                      {sortedProducts[0].product_name.length > 30
                        ? sortedProducts[0].product_name.substring(0, 25) + "..."
                        : sortedProducts[0].product_name}
                    </span>
                    {sortedProducts.length > 1 && (
                      <span className="more-count">
                        {" "}
                        +{sortedProducts.length - 1} more
                      </span>
                    )}
                  </div>
                  <Button
                    type="link"
                    icon={<ExpandAltOutlined />}
                    onClick={() =>
                      showActiveProducts(sortedProducts, record.name)
                    }
                    className="expand-products-btn"
                  />
                </>
              ) : (
                "No active products"
              )}
            </div>
          );
        },
      },
      {
        title: <h5 className="mb-0">Product Development Timeline</h5>,
        dataIndex: "pdt",
        key: "pdt",
        align: "center",
        width: 140,
        render: (data) =>
          renderTemplateData(
            data,
            () => navigateToPdt(data.partner_id),
            () =>
              sendReminder({
                partner_id: data.partner_id,
                template_type: "PDT",
              }),
            "pdt",
          ),
      },
      {
        title: <h5 className="mb-0">Filing Plans</h5>,
        dataIndex: "filing_plans",
        key: "filing_plans",
        align: "center",
        width: 140,
        render: (data) =>
          renderTemplateData(
            data,
            () => navigateToFilingPlans(data.partner_id),
            () =>
              sendReminder({
                partner_id: data.partner_id,
                template_type: "Filing Plans",
              }),
            "filing-plans",
          ),
      },
      {
        title: <h5 className="mb-0">Sales Report</h5>,
        dataIndex: "sales_report",
        key: "sales_report",
        align: "center",
        width: 140,
        render: (data) =>
          renderTemplateData(
            data,
            () => navigateToSalesReport(data.partner_id),
            () =>
              sendReminder({
                partner_id: data.partner_id,
                template_type: "Sales",
              }),
            "sales",
          ),
      },
      {
        title: "Actions",
        key: "operation",
        align: "center",
        width: 100,
        render: (item) => {
          const menu = (
            <Menu style={{ padding: "10px 5px" }} className="actionListdropdown">
              <Menu.Item className="actionDropdownTitle">Actions</Menu.Item>

              <Menu.Item
                key="edit"
                onClick={() => navigateToContent(item)}
                id={"edit-partner-" + item.key}
                disabled={!isUserAdmin}
                icon={<img src={editIcon} alt="Edit" width={16} height={16} />}
              >
                Edit
              </Menu.Item>

              <Menu.Item
                key="delete"
                onClick={() => showDeletePartner(item)}
                id={"delete-partner-" + item.key}
                disabled={!isUserAdmin}
                icon={
                  <img
                    src={deleteTempIcon}
                    alt="Deactivate"
                    width={16}
                    height={16}
                  />
                }
              >
                Deactivate
              </Menu.Item>
            </Menu>
          );
          return (
            <Dropdown overlay={menu} trigger={["click"]}>
              <Button
                type="link"
                icon={<EllipsisOutlined style={{ fontSize: "20px" }} />}
              />
            </Dropdown>
          );
        },
      },
    ];
    return columns;
  };

  const handleSendReminder = async (reminderFunction) => {
    try {
      await reminderFunction().unwrap();
      message.success('Reminder sent successfully!');
    } catch (error) {
      message.error('Failed to send reminder');
    }
  };

  const renderTemplateData = (data, viewReport, sendReminder, templateName) => {
    if (has(data, "is_read")) {
      const {
        quarter_name,
        is_approved,
        submission_time,
        updated_at,
        report_status,
        partner_id,
      } = data;
      return (
        <div className="gx-flex-row gx-align-items-center gx-justify-content-center font-20">
          <Tooltip title={"For Quarter: " + quarter_name}>
            <img
              className="icon"
              src={infoIcon}
              alt="info"
            />
          </Tooltip>
          <Tooltip
            title={
              "Report " +
              report_status +
              " on " +
              moment(
                report_status === "Submitted" ? submission_time : updated_at,
              ).format("Do MMM YYYY, hh:mm A")
            }
          >
            {is_approved === null ? (
              <Checkbox
                className="ml-20 green-border-checkbox "
                checked={false}
              ></Checkbox>
            ) : is_approved ? (
              <CheckCircleTwoTone className="ml-20" twoToneColor="#52c41a" />
            ) : (
              <CloseCircleTwoTone className="ml-20" twoToneColor="#f44336" />
            )}
          </Tooltip>
          <Tooltip title="View report">
            <img
              className="icon ml-20"
              style={{ cursor: "pointer" }}
              src={eyeIcon}
              onClick={() => viewReport()}
              id={"view-" + templateName + "-" + partner_id}
              alt="eye"
            />
          </Tooltip>
        </div>
      );
    } else {
      return (
        <div
          className={`gx-flex-row gx-align-items-center gx-justify-content-center font-20 +
                    ${!isUserAdmin ? " disable-not-accessible-icons" : ""}`}
        >
          <Tooltip title="Send Reminder">
            <img
              className="icon"
              src={notificationBellIcon}
              alt="notification"
              onClick={isUserAdmin ? () => handleSendReminder(sendReminder) : ""}
              id={"reminder-" + templateName + "-" + data.partner_id}
              style={{ cursor: 'pointer' }}
            />
          </Tooltip>
        </div>
      );
    }
  };

  const showDeletePartner = (item) => {
    showDeleteConfirmation(
      `the partner "${item.name.toUpperCase()}"`,
      () => {
        deletePartner(item.key);
      },
      () => {
        // Optional cancel callback
        console.log("Delete cancelled");
      }
    );
  };

  const showDeleteLicensor = (item) => {
    showDeleteConfirmation(
      `the licensor "${(item.name || "").toUpperCase()}"`,
      () => {
        deleteLicensor(item.id);
      },
      () => {
        console.log("Delete cancelled");
      }
    );
  };

  const licensorColumns = [
    {
      title: "Licensor Name",
      dataIndex: "name",
      key: "name",
      render: (text) => text || "-",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Royalty Invoice Tracking",
      key: "can_view_royalty_invoice",
      width: 200,
      align: "center",
      render: (_, item) => (
        <Switch
          checked={!!item.can_view_royalty_invoice}
          disabled={!isUserAdmin || isUpdatingLicensor}
          onChange={(checked) =>
            handleLicensorPermissionToggle(item, "can_view_royalty_invoice", checked)
          }
        />
      ),
    },
    {
      title: "Approval Requests",
      key: "can_view_approval_requests",
      width: 180,
      align: "center",
      render: (_, item) => (
        <Switch
          checked={!!item.can_view_approval_requests}
          disabled={!isUserAdmin || isUpdatingLicensor}
          onChange={(checked) =>
            handleLicensorPermissionToggle(item, "can_view_approval_requests", checked)
          }
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      align: "center",
      render: (_, item) => (
        <Space size="middle">
          <Tooltip title="Edit licensor">
            <img
              src={editIcon}
              alt="edit"
              style={{ cursor: isUserAdmin ? "pointer" : "not-allowed", opacity: isUserAdmin ? 1 : 0.4 }}
              onClick={() => (isUserAdmin ? showEditLicensor(item) : null)}
            />
          </Tooltip>
          <Tooltip title="Delete licensor">
            <img
              src={deleteTempIcon}
              alt="delete"
              style={{ cursor: isUserAdmin ? "pointer" : "not-allowed", opacity: isUserAdmin ? 1 : 0.4 }}
              onClick={() => (isUserAdmin ? showDeleteLicensor(item) : null)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Show loading either during initial load or when fetching during pagination
  const isTableLoading = isLoading || isFetching;
  const isLicensorTableLoading = isLicensorLoading || isLicensorFetching;

  const addButton =
    activeTab === "licensor" ? (
      <Button
        id="partner-list-add-licensor"
        onClick={showAddLicensor}
        type="primary"
        disabled={!isUserAdmin}
      >
        <UsergroupAddOutlined /> <IntlMessages id="partner.list.addLicensor" />
      </Button>
    ) : (
      <Button
        id="partner-list-add-partner"
        onClick={navigateToAddPartner}
        type="primary"
        disabled={!isUserAdmin}
      >
        <UsergroupAddOutlined /> <IntlMessages id="partner.list.addPartner" />
      </Button>
    );

  return (
    <div className="partner-list-container">
      <Row>
        <Col span={24}>
          <h1 className="title gx-mb-4">
            <IntlMessages id="sidebar.partnerList" />
          </h1>
        </Col>

        <Col span={24}>
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            tabBarExtraContent={addButton}
          >
            <TabPane tab={<IntlMessages id="partner.list.tab.partner" />} key="partner">
              {isTableLoading ? (
                <LoadingBar loading={isTableLoading} />
              ) : (
                <Table
                  className="gx-table-responsive mpp-list-table"
                  columns={setColumns()}
                  dataSource={data}
                  bordered
                  pagination={false}
                  onChange={handleTableChange}
                  scroll={{
                    y: 'calc(100vh - 250px)',
                  }}
                  sticky={{
                    offsetHeader: 0
                  }}
                />
              )}

              {/* Custom Pagination */}
              <CustomPagination
                current={pagination.current}
                total={totalCount}
                pageSize={pagination.pageSize}
                onChange={(page, pageSize) => {
                  setPagination(prev => ({
                    ...prev,
                    current: page || 1,
                    pageSize: pageSize || prev.pageSize,
                  }));
                }}
                showSizeChanger={true}
                pageSizeOptions={['10', '20', '50', '100']}
              />
            </TabPane>

            <TabPane tab={<IntlMessages id="partner.list.tab.licensor" />} key="licensor">
              {isLicensorTableLoading ? (
                <LoadingBar loading={isLicensorTableLoading} />
              ) : (
                <Table
                  className="gx-table-responsive mpp-list-table"
                  columns={licensorColumns}
                  dataSource={licensorData}
                  rowKey="id"
                  bordered
                  pagination={false}
                  scroll={{
                    y: 'calc(100vh - 250px)',
                  }}
                  sticky={{
                    offsetHeader: 0
                  }}
                />
              )}

              {/* Custom Pagination */}
              <CustomPagination
                current={licensorPagination.current}
                total={licensorTotalCount}
                pageSize={licensorPagination.pageSize}
                onChange={(page, pageSize) => {
                  setLicensorPagination(prev => ({
                    ...prev,
                    current: page || 1,
                    pageSize: pageSize || prev.pageSize,
                  }));
                }}
                showSizeChanger={true}
                pageSizeOptions={['10', '20', '50', '100']}
              />
            </TabPane>
          </Tabs>
        </Col>
      </Row>

      <Modal
        title={productTitle}
        visible={activeProductsVisible}
        onCancel={() => setActiveProductsVisible(false)}
        footer={null}
        centered
        className="products-modal"
        closable={true}
        closeIcon={<CloseOutlined className="modal-close-icon" />}
        destroyOnClose
        width={500}
      >
        <List
          className="product-list"
          dataSource={selectedProducts}
          renderItem={(item) => (
            <List.Item className="product-item">
              <div className="product-name">{item.product_name}</div>
              {item.related_product && (
                <>
                  <ArrowRightOutlined className="product-arrow" />
                  <div className="product-name">{item.related_product}</div>
                </>
              )}
            </List.Item>
          )}
        />
      </Modal>

      {/* Confirmation Popup */}
      <ConfirmationPopup
        visible={confirmationVisible}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        confirmText={confirmationConfig.confirmText}
        cancelText={confirmationConfig.cancelText}
        confirmButtonType={confirmationConfig.confirmButtonType}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* Partner Drawer */}
      <PartnerDrawer
        visible={drawerVisible}
        onClose={handleDrawerClose}
        editMode={editMode}
        partnerId={selectedPartnerId}
      />

      {/* Licensor Drawer */}
      <LicensorDrawer
        visible={licensorDrawerVisible}
        onClose={handleLicensorDrawerClose}
        editMode={licensorEditMode}
        licensor={selectedLicensor}
      />
    </div>
  );
};

export default PartnerList;